import { useEffect, useState } from "react";

interface UseCountAnimationOptions {
  end: number;
  duration?: number;
  start?: number;
  enabled?: boolean;
}

export const useCountAnimation = ({
  end,
  duration = 2000,
  start = 0,
  enabled = true,
}: UseCountAnimationOptions) => {
  const [count, setCount] = useState(start);

  useEffect(() => {
    if (!enabled || end === start) {
      setCount(end);
      return;
    }

    let animationFrameId: number | undefined;
    const startTime = Date.now();
    const difference = end - start;

    const updateCount = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation (ease-out)
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(start + difference * easedProgress);

      setCount(currentCount);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(updateCount);
      } else {
        setCount(end);
      }
    };

    animationFrameId = requestAnimationFrame(updateCount);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [end, duration, start, enabled]);

  return count;
};
